//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import { Box } from '@locus-taxy/neo-ui';

import { useTranslation } from 'react-i18next';
import theme from './Incremental.scss';
import classnames from 'classnames';

import {
    PaginatedTable,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';

import IncrementalPjpNavigationBar from './IncrementalPjpNavigationBar';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import useIncrementalEntityState, {
    INCREMENTAL_PLAN_INDEX_MAP
} from './useIncrementalEntityState';
import useTableHandlers from './useTableHandlers';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { Loader } from 'common/components/core/loader';

type Props = {
    getNewEntity: () => Promise<void>,
    getExistingEntity: () => Promise<void>,
    EntityModel: Object,
    filters: FilterListBuilder,
    defaultColumns: [],
    entitySettingsName: string,
    existingTabTitle: string,
    newTabTitle: string,
    onNext: ({
        existingSelectedRows: Array<Immutable.Map<*, *>>,
        newSelectedRows: Array<Immutable.Map<*, *>>
    }) => void,
    customActions: {
        onPrev?: () => void,
        onCancel: (shouldRefresh?: boolean) => void
    },
    match: { url: string },
    history: { push: (url: string) => void },
    reloadData: () => void,
    allowExistingSelection: boolean,
    allowNewSelection: boolean,
    unitConverter: UnitConversionHelper
};

const IncrementalPjpDataSelectionTable = (props: Props): React.Node => {
    const {
        activeTabIndex,
        setActiveTabIndex,
        isLoading,
        teams,
        skills,
        isGlobalPersonnel,
        newEntitiesCount,
        existingEntitiesCount,
        onTableStateChange,
        data,
        toggleRowSelection,
        toggleSelectAll,
        toggleSelectPage,
        rowSelectedType,
        selectedRowCount,
        removedRowsCount,
        getExistingSelectedRows,
        getNewSelectedRows,
        newLoading,
        existingLoading,
        allEntitiesCount
    } = useIncrementalEntityState(props);

    const { unitConverter } = props;

    const disableRowSelection = (() => {
        if (
            props.allowExistingSelection &&
            activeTabIndex === INCREMENTAL_PLAN_INDEX_MAP.EXISTING_ENTITIES
        ) {
            return false;
        }
        if (
            props.allowNewSelection &&
            activeTabIndex === INCREMENTAL_PLAN_INDEX_MAP.NEW_ENTITIES
        ) {
            return false;
        }
        return true;
    })();

    const { onColumnFreeze, columns, frozenColumns } = useTableHandlers({
        props,
        toggleSelectAll,
        rowSelectedType,
        toggleSelectPage,
        disableRowSelection,
        isNewEntitiesTable:
            activeTabIndex === INCREMENTAL_PLAN_INDEX_MAP.NEW_ENTITIES
    });

    const { t } = useTranslation('entityManagement');

    const isRowSelected = React.useCallback(
        (rowId): boolean => {
            if (
                activeTabIndex === INCREMENTAL_PLAN_INDEX_MAP.EXISTING_ENTITIES
            ) {
                return !data.selectedRowIds.includes(rowId);
            }
            return data.selectedRowIds.includes(rowId);
        },
        [activeTabIndex, data.selectedRowIds]
    );

    const getTdProps = React.useCallback(
        (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const { original } = rowInfo;
            const { id } = original;

            const isChecked = isRowSelected(id);
            return {
                onCheckboxClick: selected => {
                    toggleRowSelection(id, selected);
                },
                disabled: disableRowSelection,
                isChecked,
                className: classnames({
                    [theme.removedRow]: !isChecked,
                    [theme.addedRow]: isChecked
                }),
                theme,
                t,
                teams,
                skills,
                isGlobalPersonnel,
                unitConverter
            };
        },
        [
            disableRowSelection,
            isGlobalPersonnel,
            isRowSelected,
            skills,
            t,
            teams,
            toggleRowSelection,
            unitConverter
        ]
    );

    const onTabsChange = React.useCallback(
        (e, value) => setActiveTabIndex(value),
        [setActiveTabIndex]
    );

    const onNext = React.useCallback(() => {
        props.onNext({
            existingSelectedRows: getExistingSelectedRows(),
            newSelectedRows: getNewSelectedRows()
        });
    }, [getExistingSelectedRows, getNewSelectedRows, props]);

    const { customActions } = props;

    const onCancelCreatePjp = React.useCallback(() => {
        customActions.onCancel(false);
    }, [customActions]);

    return (
        <Box bg="white" height="100%">
            {isLoading && <Loader active />}
            <IncrementalPjpNavigationBar
                activeTabIndex={activeTabIndex}
                onTabsChange={onTabsChange}
                existingEntitiesCount={existingEntitiesCount}
                newEntitiesCount={newEntitiesCount}
                theme={theme}
                disableNext={!allEntitiesCount}
                selectedRowCount={selectedRowCount}
                removedRowsCount={removedRowsCount}
                onNext={onNext}
                onPrev={props.customActions.onPrev}
                onCancel={onCancelCreatePjp}
                existingTabTitle={props.existingTabTitle}
                newTabTitle={props.newTabTitle}
                newDataLoading={newLoading}
                existingDataLoading={existingLoading}
            />
            <PaginatedTable
                wrapperClass={classnames({
                    [theme.existingData]:
                        activeTabIndex ===
                        INCREMENTAL_PLAN_INDEX_MAP.EXISTING_ENTITIES,
                    [theme.newData]:
                        activeTabIndex ===
                        INCREMENTAL_PLAN_INDEX_MAP.NEW_ENTITIES
                })}
                teams={teams}
                enableFiltering
                data={data.entities}
                totalPages={data.paginationInfo.numberOfPages}
                pageIndex={data.paginationInfo.page - 1}
                pageSize={data.paginationInfo.size}
                columns={columns}
                getTdProps={getTdProps}
                onTableStateChange={onTableStateChange}
                hasCheckbox
                theme={theme}
                hasFreezableColumns
                freezeInfo={frozenColumns || []}
                frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                onColumnFreeze={onColumnFreeze}
                pinTooltip={t('pinTooltip')}
                unpinTooltip={t('unpinTooltip')}
                useLocalisation
            />
        </Box>
    );
};

export default React.memo<Props>(IncrementalPjpDataSelectionTable);
